import type { AnyStateFrame, GameContract, GameTransport } from '@efira/game-sdk';
import type {
  ActionCommand,
  ChatLine,
  DirectoryMember,
  GameResyncPayload,
  HudElementSpec,
  LayoutSpecPayload,
  StageCommand,
} from '@efira/protocol';

/**
 * Шина каналов игры: то, что стоит между сокетом и смонтированной игрой.
 *
 * ЗАЧЕМ ПРОСЛОЙКА, А НЕ ПРЯМАЯ ПОДПИСКА НА СОКЕТ. Игра живёт короче
 * соединения: сцена меняется, сокет остаётся. Подписавшись на сокет напрямую,
 * игра обязана была бы отписаться сама, и это ровно тот класс утечки, против
 * которого построен весь контракт сцены: забытая подписка держит объект игры
 * живым, а проявится это у стримера на четвёртом часу эфира после двадцатой
 * смены игры.
 *
 * Второе следствие - и оно важнее: шина ничего не знает про Socket.IO.
 * Значит, игра, работающая от шины, работает и от подставной шины в тесте,
 * без сервера и без сети.
 *
 * Третье: шина ПОМНИТ последний контракт и накопленный справочник. Игра,
 * смонтированная после их прихода, получает их сразу, а не ждёт следующего
 * изменения, которого в спокойном эфире может не быть минутами.
 */

type Listener<T> = (value: T) => void;

function channel<T>(): {
  add: (listener: Listener<T>) => () => void;
  emit: (value: T) => void;
  clear: () => void;
} {
  const listeners = new Set<Listener<T>>();

  return {
    add: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    emit: (value) => {
      for (const listener of listeners) {
        listener(value);
      }
    },
    clear: () => listeners.clear(),
  };
}

export interface OverlayBus extends GameTransport {
  /** Последний контракт или `null`, если он ещё не приезжал. */
  contract: () => GameContract | null;
  /** Накопленный справочник участников. */
  members: () => Readonly<Record<string, DirectoryMember>>;
  /**
   * Ждёт контракт указанной игры. Нужен SceneHost: монтировать игру без
   * словаря значит показать зрителю ключи локализации вместо слов.
   */
  waitForContract: (gameId: string, timeoutMs: number) => Promise<GameContract | null>;

  /** Внутреннее: вызывается соединением при получении сообщения. */
  pushContract: (contract: GameContract) => void;
  pushState: (frame: AnyStateFrame) => void;
  pushAction: (command: ActionCommand) => void;
  pushChat: (line: ChatLine) => void;
  pushMembers: (members: Record<string, DirectoryMember>) => void;
  /**
   * Команда сцене платформы.
   *
   * Через шину, а не свойством компонента: подписчик ставит состояние в
   * ОБРАБОТЧИКЕ, а не в эффекте. Установка состояния прямо в теле эффекта -
   * каскад перерисовок, и правило линтера здесь право: команда приходит из
   * внешней системы, и место ей в подписке.
   */
  pushStage: (command: StageCommand) => void;
  onStage: (listener: (command: StageCommand) => void) => () => void;
  /**
   * Сообщить серверу состав элементов сцены - для редактора раскладки.
   *
   * Объявляет их тот же код, что рисует: два описания одного и того же
   * разошлись бы молча, и редактор двигал бы элемент, которого в кадре нет.
   */
  reportLayout: (payload: LayoutSpecPayload) => void;
  /**
   * Подвижные части, которые рисует НЕ ИГРА: виджеты оверлея.
   *
   * Они объявляются тем же составом, что и части сцены, - иначе редактор
   * раскладки их не увидит и двигать их будет нечем. Отдельным вызовом,
   * потому что приходят они из другого места и в другой момент: сцена
   * сообщает свои части при монтировании, виджеты - при каждой правке
   * набора.
   */
  /**
   * Подвижные части, объявленные НЕ СЦЕНОЙ: виджеты и части платформы.
   *
   * Группа - первый аргумент: хостов несколько (виджеты и сцена платформы),
   * и список одного не должен затирать список другого. До этого затирал:
   * кто смонтировался последним, того редактор и видел.
   */
  setLayoutExtras: (group: string, elements: readonly HudElementSpec[]) => void;

  /** Внутреннее: как отправить запрос ресинхронизации. Ставит соединение. */
  setResync: (send: (payload: GameResyncPayload) => void) => void;
  /** Внутреннее: как отправить состав элементов. Ставит соединение. */
  setReportLayout: (send: (payload: LayoutSpecPayload) => void) => void;
  dispose: () => void;
}

export function createBus(): OverlayBus {
  const contracts = channel<GameContract>();
  const states = channel<AnyStateFrame>();
  const actions = channel<ActionCommand>();
  const chat = channel<ChatLine>();
  const membersChannel = channel<Record<string, DirectoryMember>>();
  const stage = channel<StageCommand>();

  let lastContract: GameContract | null = null;
  const directory: Record<string, DirectoryMember> = {};
  let send: ((payload: GameResyncPayload) => void) | null = null;
  let sendLayout: ((payload: LayoutSpecPayload) => void) | null = null;
  /** Последний отправленный состав: повтор при переподключении не нужен. */
  let reported: string | null = null;
  /** Последний состав сцены и подвижные части виджетов - шлются вместе. */
  let lastReport: LayoutSpecPayload | null = null;
  const extraGroups = new Map<string, readonly HudElementSpec[]>();

  /**
   * Отправить состав ЦЕЛИКОМ: части сцены плюс виджеты.
   *
   * Двумя сообщениями это не выразить: сервер хранит один состав на игру, и
   * второе сообщение затёрло бы первое - редактор показал бы либо только
   * сцену, либо только виджеты.
   */
  const flushLayout = () => {
    if (sendLayout === null || lastReport === null) {
      return;
    }

    const elements = [...lastReport.elements, ...[...extraGroups.values()].flat()];
    const key = `${lastReport.gameId}:${elements.map((item) => item.id).join(',')}`;

    // Состав меняется вместе с версией игры и набором виджетов: слать его при
    // каждом переподключении browser source значит платить за одно и то же.
    if (reported === key) {
      return;
    }

    reported = key;
    sendLayout({ gameId: lastReport.gameId, elements });
  };

  return {
    onContract: contracts.add,
    onState: states.add,
    onAction: actions.add,
    onChat: chat.add,
    onMembers: membersChannel.add,

    resync: (reason, since) => {
      const gameId = lastContract?.gameId;

      if (gameId === undefined || send === null) {
        return;
      }

      send({ gameId, since, reason });
    },

    contract: () => lastContract,
    members: () => directory,

    waitForContract: (gameId, timeoutMs) => {
      if (lastContract?.gameId === gameId) {
        return Promise.resolve(lastContract);
      }

      return new Promise((resolve) => {
        const timer = window.setTimeout(() => {
          off();
          resolve(null);
        }, timeoutMs);

        const off = contracts.add((contract) => {
          if (contract.gameId !== gameId) {
            return;
          }

          window.clearTimeout(timer);
          off();
          resolve(contract);
        });
      });
    },

    pushContract: (contract) => {
      lastContract = contract;
      contracts.emit(contract);
    },
    pushState: states.emit,
    pushAction: actions.emit,
    pushStage: stage.emit,
    onStage: stage.add,
    pushChat: chat.emit,
    pushMembers: (incoming) => {
      Object.assign(directory, incoming);
      membersChannel.emit(incoming);
    },

    reportLayout: (payload) => {
      lastReport = payload;
      flushLayout();
    },

    setLayoutExtras: (group, elements) => {
      extraGroups.set(group, elements);
      flushLayout();
    },

    setResync: (next) => {
      send = next;
    },

    setReportLayout: (next) => {
      sendLayout = next;
    },

    dispose: () => {
      contracts.clear();
      states.clear();
      actions.clear();
      chat.clear();
      membersChannel.clear();
      stage.clear();
      lastContract = null;
      send = null;
      sendLayout = null;
      reported = null;

      for (const key of Object.keys(directory)) {
        delete directory[key];
      }
    },
  };
}
